/**
 * Jurisdictions that require naming the original creditor (typically the
 * underwriting bank) on account-origin surfaces.
 *
 * - Washington and Illinois: entire state
 * - New York City and Yonkers: city-level via ZIP allowlists / ranges
 * - New York with no ZIP: original-creditor copy (city cannot be proven)
 */

export const STATE_LEVEL_JURISDICTIONS = new Set(["WA", "IL"]);

/**
 * City-level origination (NYC / Yonkers) needs a ZIP. When Persist has this
 * state but no ZIP, show the longer original-creditor copy rather than
 * falling back to Interprose geo.
 */
export const ZIP_GATED_FALLBACK_STATES = new Set(["NY"]);

/** Yonkers, NY */
export const YONKERS_ZIP_CODES = new Set([
  "10701",
  "10703",
  "10704",
  "10705",
  "10706",
  "10707",
  "10708",
  "10710",
]);

/**
 * New York City five boroughs as inclusive 5-digit ZIP ranges.
 * Queens is split so Nassau County prefixes (notably 115xx and most 110xx)
 * are not treated as NYC.
 *
 * Manhattan: 10001–10282
 * Bronx: 10451–10475
 * Brooklyn: 11201–11256
 * Queens: 11004–11005, 11101–11109, 11351–11436, 11691–11697
 * Staten Island: 10301–10314
 */
export const NYC_ZIP_RANGES = [
  [10001, 10282],
  [10451, 10475],
  [11201, 11256],
  [11004, 11005],
  [11101, 11109],
  [11351, 11436],
  [11691, 11697],
  [10301, 10314],
];

const STATE_NAME_TO_CODE = {
  washington: "WA",
  illinois: "IL",
  "new york": "NY",
};

export const normalizeStateCode = (value) => {
  const normalized = String(value || "")
    .trim()
    .toUpperCase();
  if (normalized.length === 2) return normalized;

  const fromName = STATE_NAME_TO_CODE[String(value || "").trim().toLowerCase()];
  return fromName || "";
};

export const normalizeZipCode = (value) => {
  const digits = String(value || "").replace(/\D/g, "");
  if (digits.length < 5) return "";
  return digits.slice(0, 5);
};

const isZipInInclusiveRange = (zip, [start, end]) => {
  const numeric = Number.parseInt(zip, 10);
  return Number.isFinite(numeric) && numeric >= start && numeric <= end;
};

export const isNewYorkCityZip = (zip) =>
  Boolean(zip) && NYC_ZIP_RANGES.some((range) => isZipInInclusiveRange(zip, range));

export const isYonkersZip = (zip) => Boolean(zip) && YONKERS_ZIP_CODES.has(zip);

/**
 * NYC / Yonkers ZIPs imply NY even when Persist still has a stale state code.
 */
export const inferStateCodeFromZip = (zip) => {
  const normalizedZip = normalizeZipCode(zip);
  if (isNewYorkCityZip(normalizedZip) || isYonkersZip(normalizedZip)) {
    return "NY";
  }
  return "";
};

/**
 * Normalize a state/ZIP pair so city-level NY ZIPs cannot keep a conflicting
 * non-NY state (e.g. stale Persist CA + Bronx ZIP).
 */
export const coerceAccountOriginGeo = ({
  geographicState,
  geographicZip,
  state,
  zip,
  zipCode,
  postalCode,
} = {}) => {
  const normalizedZip = normalizeZipCode(
    geographicZip || zip || zipCode || postalCode
  );
  const inferredState = inferStateCodeFromZip(normalizedZip);
  const normalizedState =
    inferredState || normalizeStateCode(geographicState || state) || null;
  return {
    geographicState: normalizedState,
    geographicZip: normalizedZip || null,
  };
};

const readLocationZipAndState = (location = {}) => {
  const zip = normalizeZipCode(
    location.geographicZip ||
      location.zip ||
      location.zipCode ||
      location.postalCode ||
      location.mailingZip
  );
  const state = normalizeStateCode(location.geographicState || location.state);
  return { zip, state };
};

/**
 * WA / IL / NYC / Yonkers only. Used when choosing disclosure display geo.
 * Persist NY with no ZIP is not a display jurisdiction.
 */
export const accountOriginIdentifiesGatedJurisdiction = (location = {}) => {
  const { zip, state } = readLocationZipAndState(location);
  if (isNewYorkCityZip(zip) || isYonkersZip(zip)) {
    return true;
  }
  return STATE_LEVEL_JURISDICTIONS.has(state);
};

/**
 * @param {{ geographicState?: string, geographicZip?: string, zip?: string, zipCode?: string, postalCode?: string }} location
 * @returns {boolean}
 */
export const accountOriginRequiresOriginalCreditor = (location = {}) => {
  if (accountOriginIdentifiesGatedJurisdiction(location)) {
    return true;
  }

  const { zip, state } = readLocationZipAndState(location);
  // Persist often has NY with no ZIP. City lists cannot be proven, so use the
  // longer original-creditor copy for that state until a ZIP is present.
  return !zip && ZIP_GATED_FALLBACK_STATES.has(state);
};

/**
 * Pull a ZIP from common account / Persist / Interprose field shapes for
 * jurisdiction gating. Used only for location rules — not displayed as copy.
 */
export const resolveAccountGeographicZip = (account = {}) =>
  normalizeZipCode(
    account.geographicZip ||
      account.zip ||
      account.zipCode ||
      account.postalCode ||
      account.mailingZip ||
      account.mailingZipCode ||
      account.originalPrimaryZip ||
      account.debtorZip ||
      account.primaryZip ||
      account.primaryZipCode ||
      account.customerZip ||
      account.address?.zip ||
      account.address?.zipCode ||
      account.address?.postalCode ||
      account.consumerAddress?.zip ||
      account.consumerAddress?.zipCode ||
      account.consumerAddress?.postalCode ||
      account.demographics?.zip ||
      account.demographics?.zipCode ||
      account.primaryDemographic?.zip ||
      account.primaryDemographic?.zipCode
  );

/**
 * Pull a state code from common account / Persist / Interprose field shapes.
 */
export const resolveAccountGeographicState = (account = {}) =>
  normalizeStateCode(
    account.geographicState ||
      account.state ||
      account.mailingState ||
      account.originalPrimaryState ||
      account.debtorState ||
      account.primaryState ||
      account.customerState ||
      account.stateCode ||
      account.address?.state ||
      account.consumerAddress?.state ||
      account.demographics?.state ||
      account.primaryDemographic?.state
  );

/**
 * Choose display geo when Persist and Interprose/consumer disagree.
 *
 * Prefer the source that identifies a WA / IL / NYC / Yonkers jurisdiction.
 * Persist NY with no ZIP does not count here — that rule is origination-only.
 * When Persist has a stale state without ZIP (common SAMPLE2 primary_state_code
 * CA case), prefer consumer state/ZIP so city disclosures match the live
 * Interprose address.
 */
export const resolvePreferredAccountOriginGeo = ({
  overlay = {},
  consumer = {},
} = {}) => {
  const overlayGeo = coerceAccountOriginGeo(overlay);
  const consumerGeo = coerceAccountOriginGeo(consumer);
  const overlayState = overlayGeo.geographicState || "";
  const overlayZip = overlayGeo.geographicZip || "";
  const consumerState = consumerGeo.geographicState || "";
  const consumerZip = consumerGeo.geographicZip || "";

  const overlayRequires = accountOriginIdentifiesGatedJurisdiction({
    geographicState: overlayState,
    geographicZip: overlayZip,
  });
  const consumerRequires = accountOriginIdentifiesGatedJurisdiction({
    geographicState: consumerState,
    geographicZip: consumerZip,
  });

  if (consumerRequires && !overlayRequires) {
    return {
      geographicState: consumerState || null,
      geographicZip: consumerZip || overlayZip || null,
    };
  }

  if (overlayRequires && !consumerRequires) {
    return {
      geographicState: overlayState || null,
      geographicZip: overlayZip || consumerZip || null,
    };
  }

  if (!overlayZip && consumerZip) {
    return {
      geographicState: consumerState || null,
      geographicZip: consumerZip,
    };
  }

  if (
    consumerState &&
    overlayState &&
    consumerState !== overlayState &&
    !overlayZip
  ) {
    return {
      geographicState: consumerState,
      geographicZip: consumerZip || null,
    };
  }

  // Prefer consumer when both have geo but states conflict (updated address).
  if (
    consumerState &&
    overlayState &&
    consumerState !== overlayState &&
    consumerZip
  ) {
    return {
      geographicState: consumerState,
      geographicZip: consumerZip,
    };
  }

  return {
    geographicState: overlayState || consumerState || null,
    geographicZip: overlayZip || consumerZip || null,
  };
};
